import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { PageBreadcrumbComponent } from '../../../shared/components/common/page-breadcrumb/page-breadcrumb.component';
import { AuthService } from '../../auth-pages/services/auth.service';
import { ChatflowApiService } from '../services/chatflow-api.service';
import { extractApiErrorMessage } from '../services/chatflow-error.util';
import { ChatflowTemplateApiService } from '../services/chatflow-template-api.service';
import {
  ChatflowFlowValidationResponse,
  ChatflowRequest,
  ChatflowResponse,
  ChatflowTemplateDetailResponse,
  ChatflowTemplateResponse,
  ChatflowType
} from '../models/chatflow.model';
import {
  CHATFLOW_PRIORITY_LEVELS,
  ChatflowPriorityLevel,
  getChatflowCategoryLabel,
  getChatflowPriorityLabel,
  normalizeChatflowPriority
} from '../services/chatflow-label.util';
import { ToastService } from '../../../shared/services/toast.service';

@Component({
  selector: 'app-chatflow-form',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterModule,
    PageBreadcrumbComponent
  ],
  templateUrl: './chatflow-form.component.html'
})
export class ChatflowFormComponent implements OnInit {
  private readonly fb = inject(FormBuilder);

  readonly chatflowTypes: ChatflowType[] = ['LIVE_CHAT', 'BOT', 'AI_CHATBOT'];
  readonly priorityLevels: ChatflowPriorityLevel[] = CHATFLOW_PRIORITY_LEVELS;

  loading = false;
  saving = false;
  validating = false;
  error = '';
  validationError = '';
  isEditing = false;
  chatflowId: number | null = null;
  currentChatflow: ChatflowResponse | null = null;
  templates: ChatflowTemplateResponse[] = [];
  templateError = '';
  selectedTemplateDetail: ChatflowTemplateDetailResponse | null = null;
  selectedTemplateLoading = false;
  /** Prioridad tal como está guardada en el backend (puede no coincidir con un nivel canónico). */
  originalPriority: number | null = null;

  readonly form = this.fb.group({
    name: ['', [Validators.required, Validators.maxLength(120)]],
    description: ['', [Validators.maxLength(500)]],
    chatflowType: ['BOT' as ChatflowType, [Validators.required]],
    priority: [100, [Validators.required, Validators.min(0)]],
    templateId: [null as number | null]
  });

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private authService: AuthService,
    private chatflowApi: ChatflowApiService,
    private templateApi: ChatflowTemplateApiService,
    private toast: ToastService
  ) {}

  ngOnInit(): void {
    this.cargarTemplates();

    this.form.get('templateId')?.valueChanges.subscribe((templateId) => {
      this.form.get('chatflowType')?.enable();
      if (templateId) {
        this.cargarDetalleTemplate(templateId);
        
        const template = this.templates.find((item) => item.id === templateId);
        if (template?.defaultChatflowType) {
          this.form.patchValue({ chatflowType: template.defaultChatflowType }, { emitEvent: false });
          this.form.get('chatflowType')?.disable();
        }
      } else {
        this.selectedTemplateDetail = null;
      }
    });

    const id = Number(this.route.snapshot.paramMap.get('id'));
    if (id) {
      this.isEditing = true;
      this.chatflowId = id;
      this.cargarChatflow(id);
    }
  }

  cargarTemplates(): void {
    this.templateError = '';
    this.templateApi.getTemplates().subscribe({
      next: (data) => {
        this.templates = [...data].sort((a, b) => (a.sortOrder || 9999) - (b.sortOrder || 9999));
        const currentTemplateId = this.form.get('templateId')?.value;
        if (currentTemplateId) {
          this.cargarDetalleTemplate(currentTemplateId);
        }
      },
      error: (err) => {
        this.templates = [];
        this.templateError = extractApiErrorMessage(err, 'No se pudieron cargar las plantillas');
      }
    });
  }

  cargarChatflow(id: number): void {
    this.loading = true;
    this.error = '';

    this.chatflowApi.getChatflowById(id).subscribe({
      next: (chatflow) => {
        this.currentChatflow = chatflow;
        this.originalPriority = chatflow.priority;
        this.form.patchValue({
          name: chatflow.name,
          description: chatflow.description || '',
          chatflowType: chatflow.chatflowType,
          priority: normalizeChatflowPriority(chatflow.priority),
          templateId: chatflow.templateId || null
        });
        this.loading = false;
      },
      error: (err) => {
        this.error = extractApiErrorMessage(err, 'No se pudo cargar el chatflow');
        this.loading = false;
      }
    });
  }

  cargarDetalleTemplate(templateId: number): void {
    this.selectedTemplateLoading = true;
    this.templateApi.getTemplateById(templateId).subscribe({
      next: (detail) => {
        this.selectedTemplateDetail = detail;
        this.selectedTemplateLoading = false;
      },
      error: () => {
        this.selectedTemplateDetail = null;
        this.selectedTemplateLoading = false;
      }
    });
  }

  seleccionarTemplate(template: ChatflowTemplateResponse): void {
    const actual = this.form.get('templateId')?.value;
    if (actual === template.id) {
      this.form.patchValue({ templateId: null });
    } else {
      this.form.patchValue({
        templateId: template.id,
        chatflowType: template.defaultChatflowType || this.form.get('chatflowType')?.value || 'BOT'
      });
    }
  }

  guardar(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const businessId = this.authService.getBusinessId() || 1;
    const formValue = this.form.getRawValue();

    const request: ChatflowRequest = {
      businessId,
      name: formValue.name?.trim() || '',
      description: formValue.description?.trim() || undefined,
      chatflowType: formValue.chatflowType || undefined,
      priority: Number(formValue.priority ?? 0),
      templateId: formValue.templateId || undefined
    };

    this.saving = true;
    this.error = '';

    const operation = this.isEditing && this.chatflowId
      ? this.chatflowApi.updateChatflow(this.chatflowId, request)
      : this.chatflowApi.createChatflow(request);

    operation.subscribe({
      next: (chatflow) => {
        this.saving = false;
        if (this.isEditing) {
          this.toast.updated('Chatflow');
        } else {
          this.toast.created('Chatflow');
        }
        this.router.navigate(['/chatflows', chatflow.id, 'builder']);
      },
      error: (err) => {
        const msg = extractApiErrorMessage(err, 'No se pudo guardar el chatflow');
        this.error = msg;
        this.toast.error({ title: 'Error', message: msg });
        this.saving = false;
      }
    });
  }

  validarPublicacion(): void {
    if (!this.chatflowId) {
      this.validationError = 'Guarda el chatflow antes de validar su publicación.';
      return;
    }

    this.validating = true;
    this.validationError = '';

    this.chatflowApi.validatePublish(this.chatflowId).subscribe({
      next: () => {
        this.chatflowApi.getChatflowById(this.chatflowId!).subscribe({
          next: (chatflow) => {
            this.currentChatflow = chatflow;
            this.validating = false;
          },
          error: () => {
            this.validating = false;
          }
        });
      },
      error: (err) => {
        this.validationError = extractApiErrorMessage(err, 'No se pudo validar el chatflow');
        this.validating = false;
      }
    });
  }

  cancelar(): void {
    this.router.navigate(['/chatflows']);
  }

  /** Copia el identificador interno (slug técnico) al portapapeles. */
  copiarIdentificador(): void {
    const identificador = this.currentChatflow?.identifier;
    if (!identificador) return;
    navigator.clipboard?.writeText(identificador).then(() =>
      this.toast.info(`Identificador "${identificador}" copiado al portapapeles.`)
    );
  }

  get pageTitle(): string {
    return this.isEditing ? 'Editar chatflow' : 'Nuevo chatflow';
  }

  getTypeLabel(type: ChatflowType): string {
    if (type === 'LIVE_CHAT') return 'Chat en vivo';
    if (type === 'AI_CHATBOT') return 'Chatbot IA';
    return 'Bot';
  }

  getCategoryLabel(category?: string): string {
    return getChatflowCategoryLabel(category);
  }

  /**
   * Nota de compatibilidad: si el valor guardado no coincide con un nivel canónico,
   * indica cómo se está interpretando por rango.
   */
  get priorityAdjustedNote(): string {
    if (this.originalPriority === null) return '';
    const esNivelCanonico = this.priorityLevels.some((level) => level.value === this.originalPriority);
    if (esNivelCanonico) return '';
    return `Valor guardado: ${this.originalPriority} (se interpreta como nivel "${getChatflowPriorityLabel(this.originalPriority)}"). Al guardar se ajustará al nivel seleccionado.`;
  }

  getTypeSummary(type: ChatflowType): string {
    if (type === 'LIVE_CHAT') {
      return 'Para atención humana en tiempo real, con posibilidad de precalificación y derivación a agentes.';
    }
    if (type === 'AI_CHATBOT') {
      return 'Para respuestas automáticas apoyadas en artículos publicados y contexto de IA del backend.';
    }
    return 'Para flujos automatizados con nodos, preguntas, capturas y acciones sobre CRM.';
  }

  hasError(field: string): boolean {
    const control = this.form.get(field);
    return !!control && control.invalid && control.touched;
  }

  getError(field: string): string {
    const control = this.form.get(field);
    if (!control?.errors) return '';
    if (control.errors['required']) return 'Este campo es obligatorio.';
    if (control.errors['maxlength']) return `Máximo ${control.errors['maxlength'].requiredLength} caracteres.`;
    if (control.errors['min']) return 'El valor debe ser mayor o igual a 0.';
    return 'Valor inválido.';
  }

  isTemplateSelected(templateId: number): boolean {
    return this.form.get('templateId')?.value === templateId;
  }

  get selectedType(): ChatflowType {
    return this.form.getRawValue().chatflowType || 'BOT';
  }

  get templateDefinitionPreview(): string {
    const raw = this.selectedTemplateDetail?.definitionJson?.trim();
    if (!raw) return '';
    return raw.length > 900 ? `${raw.slice(0, 900)}...` : raw;
  }

  get currentStatusLabel(): string {
    const status = this.currentChatflow?.status || 'DRAFT';
    if (status === 'ACTIVE') return 'Activo';
    if (status === 'ARCHIVED') return 'Archivado';
    return 'Borrador';
  }
}
